import { n as noop, h as assign, e as identity, c as compute_rest_props, s as subscribe, f as set_store_value } from "../../../../chunks/utils.js";
import { c as create_ssr_component, s as spread, b as escape_object, a as escape_attribute_value, e as escape, d as add_attribute, v as validate_component, i as each } from "../../../../chunks/ssr.js";
import { r as retire } from "../../../../chunks/api.js";
import { C as CardLayout } from "../../../../chunks/CardLayout.js";
import { B as Badge } from "../../../../chunks/Badge.js";
import { B as Button } from "../../../../chunks/Button.js";
import { S as Search } from "../../../../chunks/Search.js";
import { T as Tooltip } from "../../../../chunks/Tooltip.js";
import { T as Tabs, a as TabItem } from "../../../../chunks/Tabs.js";
import { S as SortableTable } from "../../../../chunks/SortableTable.js";
import { I as ImageRenderer } from "../../../../chunks/ImageRenderer.js";
import { L as Label } from "../../../../chunks/Label.js";
import { S as Select } from "../../../../chunks/Select.js";
import { M as Modal } from "../../../../chunks/Modal2.js";
import { c as cubicOut } from "../../../../chunks/TransitionFrame.js";
import { w as writable } from "../../../../chunks/index2.js";
import { twMerge, twJoin } from "tailwind-merge";
import { H as Heading } from "../../../../chunks/Heading.js";
import { C as CirclePlusSolid } from "../../../../chunks/CirclePlusSolid.js";
import { F as FileDrop } from "../../../../chunks/FileDrop.js";
import { g as globalToast } from "../../../../chunks/toast2.js";
import { c as currentCustomer } from "../../../../chunks/customer.js";
import { R as RemoveButton } from "../../../../chunks/RemoveButton.js";
import { F as FolderDuplicateSolid } from "../../../../chunks/FolderDuplicateSolid.js";
import { G as GridOutline } from "../../../../chunks/GridOutline.js";
import { L as ListOutline } from "../../../../chunks/ListOutline.js";
import { l as listView } from "../../../../chunks/viewStore.js";
import { g as globalConfirm } from "../../../../chunks/modal.js";
import dayjs from "dayjs";
const is_client = typeof window !== "undefined";
let now = is_client ? () => window.performance.now() : () => Date.now();
let raf = is_client ? (cb) => requestAnimationFrame(cb) : noop;
const tasks = /* @__PURE__ */ new Set();
function run_tasks(now2) {
  tasks.forEach((task) => {
    if (!task.c(now2)) {
      tasks.delete(task);
      task.f();
    }
  });
  if (tasks.size !== 0) raf(run_tasks);
}
function loop(callback) {
  let task;
  if (tasks.size === 0) raf(run_tasks);
  return {
    promise: new Promise((fulfill) => {
      tasks.add(task = { c: callback, f: fulfill });
    }),
    abort() {
      tasks.delete(task);
    }
  };
}
function is_date(obj) {
  return Object.prototype.toString.call(obj) === "[object Date]";
}
function get_interpolator(a, b) {
  if (a === b || a !== a) return () => a;
  const type = typeof a;
  if (type !== typeof b || Array.isArray(a) !== Array.isArray(b)) {
    throw new Error("Cannot interpolate values of different type");
  }
  if (Array.isArray(a)) {
    const arr = b.map((bi, i) => {
      return get_interpolator(a[i], bi);
    });
    return (t) => arr.map((fn) => fn(t));
  }
  if (type === "object") {
    if (!a || !b) throw new Error("Object cannot be null");
    if (is_date(a) && is_date(b)) {
      a = a.getTime();
      b = b.getTime();
      const delta = b - a;
      return (t) => new Date(a + t * delta);
    }
    const keys = Object.keys(b);
    const interpolators = {};
    keys.forEach((key) => {
      interpolators[key] = get_interpolator(a[key], b[key]);
    });
    return (t) => {
      const result = {};
      keys.forEach((key) => {
        result[key] = interpolators[key](t);
      });
      return result;
    };
  }
  if (type === "number") {
    const delta = b - a;
    return (t) => a + t * delta;
  }
  throw new Error(`Cannot interpolate ${type} values`);
}
function tweened(value, defaults = {}) {
  const store = writable(value);
  let task;
  let target_value = value;
  function set(new_value, opts) {
    if (value == null) {
      store.set(value = new_value);
      return Promise.resolve();
    }
    target_value = new_value;
    let previous_task = task;
    let started = false;
    let {
      delay = 0,
      duration = 400,
      easing = identity,
      interpolate = get_interpolator
    } = assign(assign({}, defaults), opts);
    if (duration === 0) {
      if (previous_task) {
        previous_task.abort();
        previous_task = null;
      }
      store.set(value = target_value);
      return Promise.resolve();
    }
    const start = now() + delay;
    let fn;
    task = loop((now2) => {
      if (now2 < start) return true;
      if (!started) {
        fn = interpolate(value, new_value);
        if (typeof duration === "function") duration = duration(value, new_value);
        started = true;
      }
      if (previous_task) {
        previous_task.abort();
        previous_task = null;
      }
      const elapsed = now2 - start;
      if (elapsed > /** @type {number} */
      duration) {
        store.set(value = new_value);
        return false;
      }
      store.set(value = fn(easing(elapsed / duration)));
      return true;
    });
    return task.promise;
  }
  return {
    set,
    update: (fn, opts) => set(fn(target_value, value), opts),
    subscribe: store.subscribe
  };
}
const Progressbar = create_ssr_component(($$result, $$props, $$bindings, slots) => {
  let $$restProps = compute_rest_props($$props, [
    "progress",
    "precision",
    "tweenDuration",
    "animate",
    "size",
    "labelInside",
    "labelOutside",
    "easing",
    "color",
    "labelInsideClass",
    "divClass"
  ]);
  let $_progress, $$unsubscribe__progress;
  let { progress = "45" } = $$props;
  let { precision = 0 } = $$props;
  let { tweenDuration = 400 } = $$props;
  let { animate = false } = $$props;
  let { size = "h-2.5" } = $$props;
  let { labelInside = false } = $$props;
  let { labelOutside = "" } = $$props;
  let { easing = cubicOut } = $$props;
  let { color = "primary" } = $$props;
  let { labelInsideClass = "text-primary-100 text-xs font-medium text-center p-0.5 leading-none rounded-full" } = $$props;
  let { divClass = "w-full bg-gray-200 rounded-full dark:bg-gray-700" } = $$props;
  const _progress = tweened(0, {
    duration: animate ? tweenDuration : 0,
    easing
  });
  $$unsubscribe__progress = subscribe(_progress, (value) => $_progress = value);
  const barColors = {
    primary: "bg-primary-600",
    blue: "bg-blue-600",
    gray: "bg-gray-600 dark:bg-gray-300",
    red: "bg-red-600 dark:bg-red-500",
    green: "bg-green-600 dark:bg-green-500",
    yellow: "bg-yellow-400",
    purple: "bg-purple-600 dark:bg-purple-500",
    indigo: "bg-indigo-600 dark:bg-indigo-500"
  };
  if ($$props.progress === void 0 && $$bindings.progress && progress !== void 0) $$bindings.progress(progress);
  if ($$props.precision === void 0 && $$bindings.precision && precision !== void 0) $$bindings.precision(precision);
  if ($$props.tweenDuration === void 0 && $$bindings.tweenDuration && tweenDuration !== void 0) $$bindings.tweenDuration(tweenDuration);
  if ($$props.animate === void 0 && $$bindings.animate && animate !== void 0) $$bindings.animate(animate);
  if ($$props.size === void 0 && $$bindings.size && size !== void 0) $$bindings.size(size);
  if ($$props.labelInside === void 0 && $$bindings.labelInside && labelInside !== void 0) $$bindings.labelInside(labelInside);
  if ($$props.labelOutside === void 0 && $$bindings.labelOutside && labelOutside !== void 0) $$bindings.labelOutside(labelOutside);
  if ($$props.easing === void 0 && $$bindings.easing && easing !== void 0) $$bindings.easing(easing);
  if ($$props.color === void 0 && $$bindings.color && color !== void 0) $$bindings.color(color);
  if ($$props.labelInsideClass === void 0 && $$bindings.labelInsideClass && labelInsideClass !== void 0) $$bindings.labelInsideClass(labelInsideClass);
  if ($$props.divClass === void 0 && $$bindings.divClass && divClass !== void 0) $$bindings.divClass(divClass);
  {
    _progress.set(Number(progress));
  }
  $$unsubscribe__progress();
  return `${labelOutside ? `<div${spread(
    [
      escape_object($$restProps),
      {
        class: escape_attribute_value(twMerge("flex justify-between mb-1", $$props.classLabelOutside))
      }
    ],
    {}
  )}><span class="text-base font-medium text-blue-700 dark:text-white">${escape(labelOutside)}</span> <span class="text-sm font-medium text-blue-700 dark:text-white">${escape(progress)}%</span></div>` : ``} <div${add_attribute("class", twMerge(divClass, size, $$props.class), 0)}>${labelInside ? `<div${add_attribute("class", twJoin(labelInsideClass, barColors[color]), 0)} style="${"width: " + escape($_progress, true) + "%"}">${escape($_progress.toFixed(precision))}%</div>` : `<div${add_attribute("class", twJoin(barColors[color], size, "rounded-full"), 0)} style="${"width: " + escape($_progress, true) + "%"}"></div>`}</div> `;
});
const TagsRenderer = create_ssr_component(($$result, $$props, $$bindings, slots) => {
  let { value } = $$props;
  if ($$props.value === void 0 && $$bindings.value && value !== void 0) $$bindings.value(value);
  return `<div class="flex gap-2">${!value ? `${validate_component(Badge, "Badge").$$render($$result, { color: "yellow" }, {}, {
    default: () => {
      return `Nicht kategorisiert`;
    }
  })}` : `${each(value.sort(), (tag) => {
    return `${validate_component(Badge, "Badge").$$render($$result, {}, {}, {
      default: () => {
        return `${escape(tag)}`;
      }
    })}`;
  })}`}</div>`;
});
const storageDocuments = writable([]);
const DocumentActions = create_ssr_component(($$result, $$props, $$bindings, slots) => {
  let $$unsubscribe_globalToast;
  let $$unsubscribe_storageDocuments;
  let $$unsubscribe_currentCustomer;
  $$unsubscribe_globalToast = subscribe(globalToast, (value) => value);
  $$unsubscribe_storageDocuments = subscribe(storageDocuments, (value) => value);
  $$unsubscribe_currentCustomer = subscribe(currentCustomer, (value) => value);
  let { category } = $$props;
  let uploadModal = false;
  let uploadList = [];
  const photoLoader = (accepted, rejected) => {
    console.log({ accepted, rejected });
    accepted.forEach((file) => {
      uploadList = [
        ...uploadList,
        {
          file,
          tags: category.categories.length === 1 ? category.categories[0].filter : [],
          progress: 0
        }
      ];
    });
  };
  if ($$props.category === void 0 && $$bindings.category && category !== void 0) $$bindings.category(category);
  let $$settled;
  let $$rendered;
  let previous_head = $$result.head;
  do {
    $$settled = true;
    $$result.head = previous_head;
    $$rendered = `<div class="h-full flex flex-col">${validate_component(Heading, "Heading").$$render(
      $$result,
      {
        tag: "h5",
        class: "text-neutral-600 mb-3"
      },
      {},
      {
        default: () => {
          return `<span class="text-primary-600">${escape(category.title)}</span>`;
        }
      }
    )} <div class="flex flex-col gap-3 flex-1">${validate_component(Button, "Button").$$render($$result, { size: "sm", outline: true, pill: true }, {}, {
      default: () => {
        return `${validate_component(CirclePlusSolid, "CirclePlusSolid").$$render($$result, { class: "pr-1" }, {}, {})}
            Dokument`;
      }
    })}</div></div> ${validate_component(Modal, "Modal").$$render(
      $$result,
      {
        title: "Upload",
        size: "xl",
        open: uploadModal
      },
      {
        open: ($$value) => {
          uploadModal = $$value;
          $$settled = false;
        }
      },
      {
        default: () => {
          return `${validate_component(FileDrop, "FileDrop").$$render(
            $$result,
            {
              extract: photoLoader,
              allowedFiles: [
                "jpg",
                "jpeg",
                "png",
                "pdf",
                "gif",
                "doc",
                "docx",
                "dotx",
                "ppt",
                "pptx",
                "xls",
                "xlsx"
              ],
              allowedSize: "6MB"
            },
            {},
            {}
          )} <form><div class="grid grid-cols-2 gap-3">${each(uploadList, (uploadObject) => {
            return `<div class="border rounded p-3"><div class="flex justify-end">${validate_component(Button, "Button").$$render($$result, { color: "red", size: "xs" }, {}, {
              default: () => {
                return `X`;
              }
            })}</div> <div>${escape(uploadObject.file.name)}</div> ${validate_component(Label, "Label").$$render($$result, { class: "space-y-2 mb-2" }, {}, {
              default: () => {
                return `<span data-svelte-h="svelte-1ps4a4n">Katalogisierung</span> ${validate_component(Select, "Select").$$render(
                  $$result,
                  { required: true, value: uploadObject.tags },
                  {
                    value: ($$value) => {
                      uploadObject.tags = $$value;
                      $$settled = false;
                    }
                  },
                  {
                    default: () => {
                      return `${each(category.categories.filter((x) => x.name !== "contract"), (tagedCategorie) => {
                        return `<option${add_attribute("value", tagedCategorie.filter, 0)}>${escape(tagedCategorie.filter.join(", "))}</option>`;
                      })} `;
                    }
                  }
                )} `;
              }
            })} ${validate_component(Progressbar, "Progressbar").$$render(
              $$result,
              {
                progress: uploadObject.progress,
                color: "green"
              },
              {},
              {}
            )} </div>`;
          })}</div> <div class="mt-3">${validate_component(Button, "Button").$$render($$result, { type: "submit" }, {}, {
            default: () => {
              return `Upload`;
            }
          })}</div></form>`;
        }
      }
    )}`;
  } while (!$$settled);
  $$unsubscribe_globalToast();
  $$unsubscribe_storageDocuments();
  $$unsubscribe_currentCustomer();
  return $$rendered;
});
const DocumentVersionRenderer = create_ssr_component(($$result, $$props, $$bindings, slots) => {
  let { item } = $$props;
  let open = false;
  if ($$props.item === void 0 && $$bindings.item && item !== void 0) $$bindings.item(item);
  let $$settled;
  let $$rendered;
  let previous_head = $$result.head;
  do {
    $$settled = true;
    $$result.head = previous_head;
    $$rendered = `${validate_component(Button, "Button").$$render($$result, { color: "light", size: "xs" }, {}, {
      default: () => {
        return `${escape(dayjs.unix(item.createdAt.stamp).format("DD.MM.YYYY"))}`;
      }
    })} ${validate_component(Modal, "Modal").$$render(
      $$result,
      { title: item.name, open },
      {
        open: ($$value) => {
          open = $$value;
          $$settled = false;
        }
      },
      {
        default: () => {
          return `${each(item.versions.sort((a, b) => a.createdAt.stamp - b.createdAt.stamp), (version) => {
            return `<div class="flex gap-3 justify-between"><div>${escape(dayjs.unix(version.createdAt.stamp).format("DD.MM.YYYY"))}</div> <div>${validate_component(Button, "Button").$$render($$result, { href: version.location, size: "xs" }, {}, {
              default: () => {
                return `Download`;
              }
            })}</div> </div>`;
          })} ${item.versions.length === 0 ? `<p data-svelte-h="svelte-1n4lxrf">Keine vorherigen Versionen verfügbar</p>` : ``}`;
        }
      }
    )}`;
  } while (!$$settled);
  return $$rendered;
});
const Page = create_ssr_component(($$result, $$props, $$bindings, slots) => {
  let tagsFilter;
  let filtered;
  let $documents, $$unsubscribe_documents;
  let $globalConfirm, $$unsubscribe_globalConfirm;
  let $listView, $$unsubscribe_listView;
  $$unsubscribe_globalConfirm = subscribe(globalConfirm, (value) => $globalConfirm = value);
  $$unsubscribe_listView = subscribe(listView, (value) => $listView = value);
  let documents = storageDocuments;
  $$unsubscribe_documents = subscribe(documents, (value) => $documents = value);
  let searchFilter = "";
  const accordions = [
    {
      title: "Board",
      categories: [
        {
          name: "Geschäftsführung",
          filter: ["Board", "Geschäftsführung"]
        },
        {
          name: "General",
          filter: ["Board", "General"]
        }
      ]
    },
    {
      title: "Dispo",
      categories: [
        { name: "USM", filter: ["Dispo", "USM"] },
        { name: "USB", filter: ["Dispo", "USB"] },
        { name: "USH", filter: ["Dispo", "USH"] },
        { name: "UKM", filter: ["Dispo", "UKM"] },
        { name: "UKB", filter: ["Dispo", "UKB"] },
        { name: "UKH", filter: ["Dispo", "UKH"] }
      ]
    },
    {
      title: "AUNA",
      categories: [
        { name: "USM", filter: ["AUNA", "USM"] },
        { name: "USB", filter: ["AUNA", "USB"] },
        { name: "USH", filter: ["AUNA", "USH"] },
        { name: "UKM", filter: ["AUNA", "UKM"] },
        { name: "UKB", filter: ["AUNA", "UKB"] },
        { name: "UKH", filter: ["AUNA", "UKH"] }
      ]
    },
    {
      title: "Sales",
      categories: [
        { name: "USM", filter: ["Sales", "USM"] },
        { name: "USB", filter: ["Sales", "USB"] },
        { name: "USH", filter: ["Sales", "USH"] },
        { name: "UKM", filter: ["Sales", "UKM"] },
        { name: "UKB", filter: ["Sales", "UKB"] },
        { name: "UKH", filter: ["Sales", "UKH"] }
      ]
    },
    {
      title: "FiBu",
      categories: [
        {
          name: "Ausgang",
          filter: ["AUNA", "Ausgang"]
        },
        {
          name: "Eingang",
          filter: ["AUNA", "Eingang"]
        }
      ]
    },
    {
      title: "Work",
      categories: [
        {
          name: "Recruiting",
          filter: ["Work", "Recruiting"]
        },
        {
          name: "Get-to-know",
          filter: ["Work", "Get-to-know"]
        },
        {
          name: "Street Aktion",
          filter: ["Work", "Street Aktion"]
        },
        {
          name: "Sonstiges",
          filter: ["Work", "Sonstiges"]
        }
      ]
    },
    {
      title: "Rebuddy",
      categories: [{ name: "Rebuddy", filter: ["Rebuddy"] }]
    },
    {
      title: "Legal",
      categories: [
        {
          name: "AÜV",
          filter: ["Legal", "AÜV"]
        },
        { name: "MAV", filter: ["Legal", "MAV"] },
        {
          name: "Gesetzestexte | Literatur | Referentenentwürfe",
          filter: ["Legal", "Gesetzestexte | Literatur | Referentenentwürfe"]
        },
        { name: "ABGs", filter: ["Legal", "AGB"] }
      ]
    },
    {
      title: "Remedia",
      categories: [{ name: "Remedia", filter: ["Remedia"] }]
    }
  ];
  const header = [
    {
      property: "name",
      label: "Name",
      sortable: true,
      movable: true,
      class: "w-1/2"
    },
    {
      property: "location",
      label: "Document",
      sortable: true,
      movable: true,
      component: ImageRenderer
    },
    {
      property: "index",
      label: "Version",
      sortable: true,
      movable: true,
      component: DocumentVersionRenderer,
      customSort: (a, b, c) => a.createdAt.stamp > b.createdAt.stamp ? c : -1 * c
    },
    {
      property: "tags",
      label: "Tag(s)",
      movable: true,
      component: TagsRenderer
    },
    {
      property: "uuid",
      label: "Entfernen",
      movable: true,
      component: RemoveButton,
      props: {
        onClick: (uuid) => {
          $globalConfirm.confirm("Wirklich löschen?", async () => {
            await retire("/document/" + uuid);
            set_store_value(documents, $documents = $documents.filter((x) => x.uuid !== uuid), $documents);
          });
        }
      }
    }
  ];
  const filterDocuments = () => {
    let consideredDocuments = [...$documents];
    const searchTypes = searchFilter.split(":").map((x) => x.trim());
    if (searchTypes.length > 1) {
      consideredDocuments = tagsFilter(searchTypes[1].split(" ").map((x) => x.trim()).filter((x) => x !== ""));
    }
    return consideredDocuments.filter((x) => x.name.toLowerCase().includes(searchTypes[0].toLowerCase()));
  };
  let $$settled;
  let $$rendered;
  let previous_head = $$result.head;
  do {
    $$settled = true;
    $$result.head = previous_head;
    tagsFilter = (tags) => {
      let filtered2 = [...$documents];
      for (let i = 0; i < tags.length; i++) {
        filtered2 = filtered2.filter((x) => x.tags?.some((y) => y.toLowerCase() === tags[i].toLowerCase()));
      }
      return filtered2;
    };
    filtered = searchFilter === "" ? $documents : filterDocuments();
    $$rendered = `${$$result.head += `<!-- HEAD_svelte-12y6hvv_START -->${$$result.title = `<title>Documents</title>`, ""}<!-- HEAD_svelte-12y6hvv_END -->`, ""} <div class="flex"><div class="flex-1">${$listView ? `<div class="border-b p-1 pb-2">${validate_component(Search, "Search").$$render(
      $$result,
      {
        placeholder: "Namensfilter : tag1 tag2",
        value: searchFilter
      },
      {
        value: ($$value) => {
          searchFilter = $$value;
          $$settled = false;
        }
      },
      {}
    )}</div> ${validate_component(SortableTable, "SortableTable").$$render($$result, { data: filtered, header }, {}, {
      empty: () => {
        return `<div class="px-3 py-2 text-neutral-500 flex gap-2" data-svelte-h="svelte-1ebj2v6">Leer</div>`;
      }
    })}` : `${validate_component(Tabs, "Tabs").$$render(
      $$result,
      {
        contentClass: " ",
        inactiveClasses: "p-4 text-gray-500 rounded-t-lg hover:text-gray-600 border-b-2 border-transparent hover:border-primary-400",
        activeClasses: "p-4 text-primary-600 rounded-t-lg border-b-2 border-primary-600",
        tabStyle: "underline"
      },
      {},
      {
        default: () => {
          return `${each(accordions.sort((a, b) => a.title.localeCompare(b.title)), (accordion, i) => {
            return `${validate_component(TabItem, "TabItem").$$render($$result, { open: i === 0 }, {}, {
              title: () => {
                return `<div class="relative flex" slot="title">${validate_component(FolderDuplicateSolid, "FolderDuplicateSolid").$$render($$result, {}, {}, {})} <span class="px-4">${escape(accordion.title)}</span>  </div>`;
              },
              default: () => {
                return `<div class="mt-5 space-y-3">${each(accordion.categories, (category) => {
                  return `${validate_component(CardLayout, "CardLayout").$$render(
                    $$result,
                    {
                      title: category.name,
                      accordion: true,
                      display: accordion.categories.length === 1
                    },
                    {},
                    {
                      "card-header": () => {
                        return `<div class="relative mt-1" slot="card-header">${validate_component(Badge, "Badge").$$render(
                          $$result,
                          {
                            color: "blue",
                            class: "text-xs font-bold"
                          },
                          {},
                          {
                            default: () => {
                              return `${escape(tagsFilter(category.filter).length)}`;
                            }
                          }
                        )} </div>`;
                      },
                      default: () => {
                        return `<div class="px-4 py-3 mb-5 space-y-2"><div class="flex gap-3"><div class="flex-1">${validate_component(SortableTable, "SortableTable").$$render(
                          $$result,
                          {
                            data: tagsFilter(category.filter),
                            header
                          },
                          {},
                          {
                            empty: () => {
                              return `<div class="px-3 py-2 text-neutral-500 flex gap-2" data-svelte-h="svelte-1ebj2v6">Leer</div> `;
                            }
                          }
                        )}</div> <div class="w-44 border-l border-neutral-200 pl-3">${validate_component(DocumentActions, "DocumentActions").$$render($$result, { category: accordion }, {}, {})}</div> </div></div> `;
                      }
                    }
                  )}`;
                })}</div> `;
              }
            })}`;
          })}`;
        }
      }
    )}`}</div> <div class="pr-3 pt-1 border-b pb-3 place-self-start">${validate_component(Button, "Button").$$render(
      $$result,
      {
        color: "alternative",
        pill: true,
        class: "!p-2"
      },
      {},
      {
        default: () => {
          return `${$listView ? `${validate_component(ListOutline, "ListOutline").$$render($$result, { class: "text-primary-600" }, {}, {})}` : `${validate_component(GridOutline, "GridOutline").$$render($$result, { class: "text-primary-600" }, {}, {})}`}`;
        }
      }
    )} ${validate_component(Tooltip, "Tooltip").$$render($$result, { placement: "left" }, {}, {
      default: () => {
        return `Ansicht wechseln`;
      }
    })}</div></div>`;
  } while (!$$settled);
  $$unsubscribe_documents();
  $$unsubscribe_globalConfirm();
  $$unsubscribe_listView();
  return $$rendered;
});
export {
  Page as default
};
//# sourceMappingURL=_page.svelte.js.map
